import * as React from 'react';
import { connect } from 'react-redux';
import * as uuid from 'uuid';
import { Editor } from 'react-draft-wysiwyg';
import {
    MAX_UPLOAD_SIZE_BYTES,
    STATIC_STORAGE_URL
} from '@nexxt/common/constants';
import {
    convertToRaw,
    EditorState,
    Entity,
    getDefaultKeyBinding,
    Modifier,
    SelectionState
} from 'draft-js';
import Dropzone from 'react-dropzone';
import { getDecorators } from '../../../../../common/richTextField/customButtons/BatchEditComponent';
import { UilQuestionCircle, UilUpload } from '@iconscout/react-unicons';
import ReactTooltip from 'react-tooltip';
import { OptionData } from '@nexxt/common/types';
import { uploadFile } from '../../../../../../services/media.service';
import Loader from '../../../../../common/Loader/Loader';
import { getLastAfterSplit } from '@nexxt/common/services/TextService';
import { getFilenameWithoutExtension } from '../../../../../../utils/string';

interface BatchEditorProps {
    editorState: EditorState;
    setEditorState: (editorState: EditorState) => void;
    options?: OptionData[];
    handleBatchImageUpload: (media: any) => void;
    handleSubmit: () => void;
    uploadFile: (acceptedFile: any, type: string) => Promise<any>;
}

const FILENAME_REGEX = /\[+[^\]]*\](?!.*\[)/;

const acceptFormats = {
    'image/*': ['.jpeg', '.png', '.jpg']
};

const BatchEditor: React.FC<BatchEditorProps> = (props: BatchEditorProps) => {
    const { editorState, setEditorState } = props;
    const editorRef = React.createRef();

    const [isNew, setIsNew] = React.useState(true);
    const [showLoading, setShowLoading] = React.useState(false);
    const [imageRefs, setImageRefs] = React.useState({});
    React.useEffect(() => {
        if (props.options?.length) {
            let editor = editorState;
            props.options
                .filter((x) => !x.empty)
                .forEach((opt) => {
                    editor = addBlock(opt, editor);
                });
        }
    }, [props.options?.length]);

    const onEditorStateChange = (changedEditorState: EditorState): void => {
        setEditorState(changedEditorState);
        isNew ? setIsNew(false) : showPlaceholder(changedEditorState);
    };

    const getFilenameFromMediaURLWithoutHash = (mediaURL: string) => {
        try {
            const urlWithHash = getLastAfterSplit(mediaURL, '/');

            return urlWithHash?.split('_').slice(1).join('_');
        } catch (err) {
            return '';
        }
    };

    const getLabelFromEntity = (label: string) => {
        const arrLabel = label.split('[');
        let mediaURL = '';
        if (arrLabel.length > 1) {
            const filename = label.match(FILENAME_REGEX)?.[0];
            debugger;
            mediaURL = imageRefs[filename];
            // filename ? STATIC_STORAGE_URL + `/usr/${filename}` : '';
        }
        return { label: arrLabel[0], mediaURL };
    };

    const addBlock = (opt: Partial<OptionData>, editor, onPaste = false) => {
        const currentContent = editor.getCurrentContent();
        const entity = currentContent.createEntity('BATCH_EDIT', 'IMMUTABLE', {
            ...opt
        });
        const filename = getFilenameFromMediaURLWithoutHash(opt.mediaURL);
        setImageRefs((ref) => ({ ...ref, [filename]: opt.mediaURL }));
        const entityKey = entity.getLastCreatedEntityKey();

        const emptySelectionState = SelectionState.createEmpty(
            editor.getSelection().getStartKey()
        );
        const entityText = opt.mediaURL
            ? `${opt.label} [${filename}]`
            : opt.label;

        const newSelectionState = emptySelectionState.merge({
            focusOffset: entityText.length,
            anchorOffset: 0
        });

        const newContents = Modifier.replaceText(
            currentContent,
            newSelectionState,
            entityText,
            null,
            entityKey
        );

        const editorStateWithEntity = EditorState.push(
            editor,
            newContents,
            'apply-entity'
        );

        const newLineContentState = Modifier.splitBlock(
            editorStateWithEntity.getCurrentContent(),
            editorStateWithEntity.getSelection()
        );
        const currentBlock = newLineContentState.getBlockForKey(
            editorStateWithEntity.getSelection().getEndKey()
        );
        const nextBlock = newLineContentState
            .getBlockMap()
            .toSeq()
            .skipUntil(function (v) {
                return v === currentBlock;
            })
            .rest()
            .first();
        const nextBlockKey = nextBlock.getKey();
        // const key = blockMapToFocus.last().getKey();
        const selection = SelectionState.createEmpty(nextBlockKey);
        const editorStateWithNewLine = EditorState.push(
            editorStateWithEntity,
            newLineContentState,
            'split-block'
        );
        const newEditor = EditorState.forceSelection(
            editorStateWithNewLine,
            selection
        );
        onEditorStateChange(newEditor);

        return editorStateWithNewLine;
    };

    const keyBindingFn = (e) => {
        //cannot detect tab key now
        // if (e.keyCode === 9) {
        //     e.preventDefault();
        //     console.log('tab');
        //     return '';
        // }
        if (e.key === 'Enter') {
            if (e.nativeEvent.shiftKey) {
                props.handleSubmit();
            } else {
                const currentContent = editorState.getCurrentContent();
                const blockKey = editorState.getSelection().getStartKey();
                const currentContentBlock =
                    currentContent.getBlockForKey(blockKey);
                const text = currentContentBlock.getText();
                const { label, mediaURL } = getLabelFromEntity(text);

                addBlock({ label, mediaURL }, editorState);
            }

            return '';
        }
        return getDefaultKeyBinding(e);
    };

    const handlePastedText = (text, editor: EditorState) => {
        const content = Modifier.removeRange(
            editor.getCurrentContent(),
            editor.getSelection(),
            'backward'
        );

        let currEditor = EditorState.push(editor, content, 'remove-range');
        text.split('\n')
            .filter((x) => x)
            .forEach((label) => {
                currEditor = addBlock(
                    { label: label.trim() },
                    currEditor,
                    true
                );
            });
        return true;
    };
    const handleReturn = (e, editorState) => {};

    const showPlaceholder = (editorState) => {
        if (
            !editorState.getCurrentContent().hasText() &&
            !editorState.getCurrentContent().getPlainText().length
        ) {
            setIsNew(true);
        }
    };

    const handleOnDrop = async (accepted) => {
        setShowLoading(true);
        const promiseUploader = accepted.map(async (media) => {
            const { path, filename } = await props.uploadFile(media, 'image');

            return {
                label: filename,
                mediaURL: path
            };
        });

        const medias: any = await Promise.all(promiseUploader);
        let currEditor = editorState;
        medias.forEach((media) => {
            currEditor = addBlock(
                {
                    label: getFilenameWithoutExtension(media.label),
                    mediaURL: media.mediaURL
                },
                currEditor
            );
        });
        setShowLoading(false);
    };
    // console.log(convertToRaw(editorState.getCurrentContent()));
    return (
        <div className="batch-editor-wrapper">
            {showLoading && <Loader isInline={false} />}
            <Dropzone
                noClick
                accept={acceptFormats}
                multiple
                maxSize={MAX_UPLOAD_SIZE_BYTES}
                onDrop={handleOnDrop}
            >
                {({ getRootProps, getInputProps, open, isDragActive }: any) => (
                    <div className="batch-editor--dropzone" {...getRootProps()}>
                        {isDragActive && (
                            <div className="dropzone--active">
                                <div>
                                    <UilUpload size={40} />
                                    <p>Drop your image files to add options</p>
                                </div>
                            </div>
                        )}
                        <input {...getInputProps()} />
                        {isNew && (
                            <>
                                <p
                                    className="batch-editor--custom-placeholder"
                                    onClick={() =>
                                        (
                                            editorRef?.current as any
                                        )?.focusEditor()
                                    }
                                >
                                    Type or copy and paste your options here,
                                    with each option on a new line. OR, drag and
                                    drop to{' '}
                                    <span
                                        className="batch-editor--custom-placeholder--uploader"
                                        onClick={open}
                                    >
                                        upload your images files
                                    </span>
                                    to start.
                                </p>
                            </>
                        )}

                        <Editor
                            ref={editorRef}
                            className="batch-editor"
                            wrapperClassName="draft-batch-wrapper"
                            editorClassName={'draft-batch-editor'}
                            editorState={editorState}
                            onEditorStateChange={onEditorStateChange}
                            keyBindingFn={keyBindingFn}
                            handleReturn={handleReturn}
                            toolbar={{ options: [] }}
                            placeholder=""
                            customDecorators={[
                                getDecorators({
                                    className: 'batch-edit--entity',
                                    editable: true,
                                    language: '',
                                    editorState: editorState
                                })
                            ]}
                            onBlur={() => showPlaceholder(editorState)}
                            handlePastedText={(text, html, editor) =>
                                handlePastedText(text, editor)
                            }
                        />

                        <p className="batch-editor--footer">
                            Hit{' '}
                            <span
                                className="batch-editor--footer--anchor"
                                onClick={() => {
                                    props.handleSubmit();
                                }}
                            >
                                Shift+Enter
                            </span>{' '}
                            to confirm options. Drop images into box or{' '}
                            <span
                                className="batch-editor--footer--anchor"
                                onClick={open}
                            >
                                upload here
                            </span>
                            <UilQuestionCircle
                                size={20}
                                data-tip="custom show"
                                data-for="batch-edit-helper"
                            />
                            <ReactTooltip
                                className="tooltip"
                                id="batch-edit-helper"
                                aria-haspopup="true"
                                effect="solid"
                            >
                                <span>
                                    Type or copy and paste all your options
                                    text, with each option on a new linebreak.{' '}
                                </span>
                            </ReactTooltip>
                        </p>
                    </div>
                )}
            </Dropzone>
        </div>
    );
};

const mapStateToProps = () => ({});

const mapDispatchToProps = (dispatch) => ({
    uploadFile: (acceptedFile: any, type: string) =>
        dispatch(uploadFile(acceptedFile, type))
});

export default connect(mapStateToProps, mapDispatchToProps)(BatchEditor);
